// absolute imports
import { useApolloClient, useQuery, useReactiveVar } from '@apollo/client';
import { useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ACCOUNT_UPDATE,
  EXCHANGE_UPDATE,
  EXCHANGES_UPDATE,
  WALLET_UPDATE,
  WALLETS_UPDATE,
} from '../../../data/apollo/queries';

// relative imports
import { currentAccountIdVar } from '../../../data/apollo/cache/reactiveVars';
import useIsWindowFocused from './useIsWindowFocused';

// available preset queries
const getPollingQueryPresets = (accountId) => ({
  account: {
    id: 'account',
    query: ACCOUNT_UPDATE,
    defaultVariables: {
      accountId,
    },
  },
  exchange: {
    id: 'exchange',
    query: EXCHANGE_UPDATE,
    defaultVariables: {
      accountId,
    },
  },
  exchanges: {
    id: 'exchanges',
    query: EXCHANGES_UPDATE,
    defaultVariables: {
      accountId,
    },
  },
  wallet: {
    id: 'wallet',
    query: WALLET_UPDATE,
    defaultVariables: {
      accountId,
    },
  },
  wallets: {
    id: 'wallets',
    query: WALLETS_UPDATE,
    defaultVariables: {
      accountId,
    },
  },
});

const checkMissingDefaultVariables = (defaultVariables = {}) =>
  Object.keys(defaultVariables).some((key) => defaultVariables[key] === null);

/* track update state of specified resource */
const useUpdatePolling = ({
  preset,
  query,
  variables,
  queryOptions = {},
  updateCondition = false,
  onUpdateComplete,
  onUpdateStart,
  disabled,
  pollInterval = 5000,
  refetchQueryList = [],
}) => {
  const params = useParams({ strict: false });
  const currentAccountId = useReactiveVar(currentAccountIdVar);
  const { accountId = currentAccountId } = params;
  const queryPreset = getPollingQueryPresets(accountId)[preset];
  const apolloClient = useApolloClient();
  const queryDoc = queryPreset?.query || query;
  const queryVariables = queryPreset
    ? { ...queryPreset.defaultVariables, ...variables }
    : variables;

  const queryName = queryDoc.definitions[0].name.value;

  // Check if window/tab is focused
  const windowIsActive = useIsWindowFocused();

  // throw err if hook is called without preset or query
  if (!queryPreset && !query) {
    throw new Error(
      `useUpdatePolling hook requires a query argument or preset argument of one of [${Object.keys(
        getPollingQueryPresets(),
      ).join(', ')}]`,
    );
  }

  // Ref for tracking updateCondition change
  const wasUpdatingRef = useRef(false);
  // Ref to track if polling was started (for cleanup)
  const pollingStartedRef = useRef(false);

  // Memoize skipQuery since used throughout
  const skipQuery = useMemo(
    () =>
      !updateCondition ||
      checkMissingDefaultVariables(queryPreset?.defaultVariables),
    [updateCondition, queryPreset?.defaultVariables],
  );

  const { startPolling, stopPolling } = useQuery(queryDoc, {
    variables: queryVariables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: skipQuery,
    ...queryOptions,
  });

  const handleRefetchQueries = useCallback(async () => {
    await apolloClient.refetchQueries({
      include: refetchQueryList,
    });
  }, [apolloClient, refetchQueryList]);

  // Handle updates
  useEffect(() => {
    // Only poll if window is active and condition is true
    const shouldPoll = updateCondition && windowIsActive && !disabled;

    // Check if polling is in progress by looking at active queries
    // We check this dynamically in the effect to get the most up-to-date state
    const activeQueriesMap = apolloClient.getObservableQueries([queryName]);
    const currentPollingInProgress = !![...activeQueriesMap.values()].find(
      (entry) => !!entry.pollingInfo,
    );

    // Start polling if condition is true and polling isn't already in progress
    // Check both the ref and the actual polling state
    if (
      shouldPoll &&
      !skipQuery &&
      !currentPollingInProgress &&
      !pollingStartedRef.current
    ) {
      startPolling(pollInterval);
      pollingStartedRef.current = true;
      if (onUpdateStart && !disabled && !wasUpdatingRef.current) {
        onUpdateStart();
      }
      wasUpdatingRef.current = true;
    }

    // stop poll if condition turns false
    if ((!updateCondition || !windowIsActive) && wasUpdatingRef.current) {
      if (currentPollingInProgress || pollingStartedRef.current) {
        stopPolling();
        pollingStartedRef.current = false;
      }
      // Only run completion callbacks if update condition is false
      if (!updateCondition && !disabled) {
        if (refetchQueryList.length > 0) {
          handleRefetchQueries();
        }
        if (onUpdateComplete) {
          onUpdateComplete();
        }
      }
      wasUpdatingRef.current = false;
    }

    // resume polling if update condition is still true (becomes active)
    if (
      windowIsActive &&
      updateCondition &&
      !wasUpdatingRef.current &&
      !currentPollingInProgress &&
      !disabled
    ) {
      startPolling(pollInterval);
      pollingStartedRef.current = true;
      wasUpdatingRef.current = true;
    }
  }, [
    startPolling,
    pollInterval,
    stopPolling,
    updateCondition,
    onUpdateStart,
    onUpdateComplete,
    disabled,
    preset,
    refetchQueryList.length,
    handleRefetchQueries,
    windowIsActive,
    queryPreset,
    apolloClient,
    queryName,
    skipQuery,
  ]);

  // useEffect cleanup to ensure polling stops on unmount
  useEffect(
    () => () => {
      // Always attempt to stop polling on unmount even if not polling
      if (pollingStartedRef.current) {
        stopPolling();
        pollingStartedRef.current = false;
      }
    },
    [stopPolling],
  );
};

export default useUpdatePolling;
